import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Collects new-event details and forwards the user to Google Calendar's
 * create-event template URL. Intentionally not reusing UnifiedModal — this
 * is a small purpose-built form.
 *
 * Props:
 *  - open: boolean
 *  - calendars: array of { id, summary, primary, backgroundColor } —
 *    pre-filtered by the caller to the "My Vertical Calendar" category.
 *  - onClose(): cancel the dialog
 */
const AddEventDialog = ({ open, calendars, onClose, lockCalendar = false }) => {
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState('');
  const [calendarId, setCalendarId] = useState('');

  useEffect(() => {
    if (open) {
      setTitle('');
      setWhen('');
      setCalendarId(calendars[0]?.id || '');
    }
  }, [open, calendars]);

  // Convert a Date object into Google's UTC compact format ("20260524T193000Z").
  const toGoogleUtc = (d) => {
    if (!d || Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return (
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
      `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
    );
  };

  const canSubmit = title.trim() && when && calendarId;

  const handleNext = () => {
    if (!canSubmit) return;
    const start = new Date(when);
    // Google Calendar's TEMPLATE URL requires an end time. Default to a
    // 1-hour event — the user can adjust it on the Google Calendar page.
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const params = new URLSearchParams();
    params.set('action', 'TEMPLATE');
    params.set('text', title.trim());
    params.set('dates', `${toGoogleUtc(start)}/${toGoogleUtc(end)}`);
    params.set('src', calendarId);
    const url = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        Add Event
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Date and Time"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            required
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          {lockCalendar ? (
            (() => {
              const cal = calendars.find((c) => c.id === calendarId) || calendars[0];
              return (
                <div>
                  <div
                    style={{ fontSize: 12, color: '#667085', marginBottom: 4 }}
                  >
                    Calendar
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      border: '1px solid #EAECF0',
                      borderRadius: 4,
                      background: '#F9FAFB',
                      color: '#344054',
                      fontSize: 14,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: cal?.backgroundColor || '#356AC3',
                      }}
                    />
                    {cal?.summary || 'Vertical calendar'}
                  </div>
                </div>
              );
            })()
          ) : (
            <FormControl fullWidth size="small" required>
              <InputLabel id="event-calendar-label">Choose Calendar</InputLabel>
              <Select
                labelId="event-calendar-label"
                label="Choose Calendar"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
              >
                {calendars.length === 0 ? (
                  <MenuItem value="" disabled>
                    No calendars available
                  </MenuItem>
                ) : (
                  calendars.map((cal) => (
                    <MenuItem key={cal.id} value={cal.id}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: cal.backgroundColor || '#356AC3',
                          marginRight: 8,
                        }}
                      />
                      {cal.summary}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={!canSubmit}>
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEventDialog;
